import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import { useSnackbar } from "context/SnackbarContext";
import SearchBar from "common/Documents/SearchBar/SearchBar";
import FilterBar from "common/Documents/FilterBar/FilterBar";
import ActionButtons from "common/Documents/ActionButtons/ActionButtons";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import Pagination from "@mui/material/Pagination";
import Loader from "components/Loader/Loader";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import documentTableData from "common/Documents/documents/documentTableData";
import Checkbox from "@mui/material/Checkbox";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import validateTotals from "common/Documents/utils/validateTotals";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDButton from "components/MDButton";
import FormGroup from "@mui/material/FormGroup";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import { getDocumentTypeMeta } from "common/utils/documentType";

function DocumentPage() {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    suppliers: [],
    excludeSuppliers: false,
    documentTypes: [],
    excludeDocumentTypes: false,
    dateType: "invoice",
    startDate: null,
    endDate: null,
    xmlType: "",
    searchQuery: "",
    searchField: "all",
    exactMatch: false,
  });
  const [pageSizeValue, setPageSizeValue] = useState(200);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const showSnackbar = useSnackbar();
  const { selectedCompany } = useCompany();
  const location = useLocation();
  const queryClient = useQueryClient();
  const didHydrateRef = useRef(false);

  // PDF tipo dialogo būsenos
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [documentType, setDocumentType] = useState("purchase"); // purchase or sales
  const [unitPriceInput, setUnitPriceInput] = useState(""); // Vieneto kaina

  // Recognition options dialog state
  const [recognitionDialogOpen, setRecognitionDialogOpen] = useState(false);
  const [useCompanyCode, setUseCompanyCode] = useState(true); // Default to using company code
  const [useMarkdown, setUseMarkdown] = useState(false); // Default to traditional format
  const [documentsToRecognize, setDocumentsToRecognize] = useState([]);
  // Per-document processing state
  const [processingIds, setProcessingIds] = useState(() => new Set());
  const isMountedRef = useRef(true);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // Local overlay for per-document updates to avoid cache thrash
  const [docOverlays, setDocOverlays] = useState(() => new Map());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // XML Export settings dialog state
  const [xmlExportDialogOpen, setXmlExportDialogOpen] = useState(false);
  const [xmlExportType, setXmlExportType] = useState("agnum");
  const [xmlExportWithMatches, setXmlExportWithMatches] = useState(false);
  const [selectedPozymiai, setSelectedPozymiai] = useState([]);
  const [includePvmInTotal, setIncludePvmInTotal] = useState(true); // M4 field
  const [spacyModelSize, setSpacyModelSize] = useState("md"); // spaCy model size choice
  const [matcherType, setMatcherType] = useState("llm"); // 'spacy' or 'llm'
  const [overridePvmCode, setOverridePvmCode] = useState(""); // PVM code override
  const [recalculateVat, setRecalculateVat] = useState(false); // Recalculate VAT for zero-VAT docs

  // Document details modal (mobile-friendly)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Generate pozymiai options (positions 1-20 for flexibility)
  const pozymiaiOptions = Array.from({ length: 20 }, (_, i) => ({
    id: i + 1,
    name: `${i + 1}`,
  }));

  // PVM code options (same as in suppliers)
  const pvmCodeOptions = [
    { value: "", label: t("useSupplierPvmCode") },
    {
      value: "PVM1",
      label: "PVM1 - Šalies teritorijoje patiektos prekės ir / ar suteiktos paslaugos, 21%",
    },
    {
      value: "PVM2",
      label: "PVM2 - Šalies teritorijoje patiektos prekės ir / ar suteiktos paslaugos, 9%",
    },
    {
      value: "PVM3",
      label: "PVM3 - Šalies teritorijoje patiektos prekės ir / ar suteiktos paslaugos, 5%",
    },
    { value: "PVM25", label: "PVM25 - Pirkėjas išskaito ir sumoka PVM, 21%" },
    { value: "PVM26", label: "PVM26 - Pirkėjas išskaito ir sumoka PVM, 9%" },
    { value: "PVM27", label: "PVM27 - Pirkėjas išskaito ir sumoka PVM, 5%" },
    { value: "PVM5", label: "PVM5 - Neapmokestinamas PVM" },
    { value: "PVM6", label: "PVM6 - Privatiems poreikiams tenkinti, 21%" },
    { value: "PVM7", label: "PVM7 - Privatiems poreikiams tenkinti, 9%" },
    { value: "PVM8", label: "PVM8 - Privatiems poreikiams tenkinti, 5%" },
    { value: "PVM28", label: "PVM28 - Privatiems poreikiams tenkinti, 0%" },
    { value: "PVM9", label: "PVM9 - Ilgalaikio turto pasigaminimas ir pagerinimas, 21%" },
    { value: "PVM30", label: "PVM30 - Ilgalaikio turto pasigaminimas ir pagerinimas, 9%" },
    { value: "PVM31", label: "PVM31 - Ilgalaikio turto pasigaminimas ir pagerinimas, 5%" },
    { value: "PVM32", label: "PVM32 - Sandoriai su specialia schema (marža), 21%" },
    { value: "PVM33", label: "PVM33 - Sandoriai su specialia schema (marža), 0%" },
    { value: "PVM12", label: "PVM12 - Prekių eksportas, 0%" },
    { value: "PVM13", label: "PVM13 - ES PVM mokėtojams patiektos prekės, 0%" },
    { value: "PVM14", label: "PVM14 - Kiti sandoriai, 0%" },
    {
      value: "PVM15",
      label: "PVM15 - Už Lietuvos ribų patiektos prekės ir/ar paslaugos, neapmokestinama",
    },
    { value: "PVM16", label: "PVM16 - Iš kitų valstybių narių prekių įsigijimas, 21%" },
    { value: "PVM17", label: "PVM17 - Iš kitų valstybių narių prekių įsigijimas, 9%" },
    { value: "PVM18", label: "PVM18 - Iš kitų valstybių narių prekių įsigijimas, 5%" },
    { value: "PVM35", label: "PVM35 - Iš kitų valstybių narių prekių įsigijimas, 0%" },
    { value: "PVM19", label: "PVM19 - Trikampė prekyba, neapmokestinama" },
    { value: "PVM20", label: "PVM20 - Iš užsienio valstybių paslaugos, 21%" },
    { value: "PVM37", label: "PVM37 - Iš užsienio valstybių paslaugos, 5%" },
    { value: "PVM38", label: "PVM38 - Iš užsienio valstybių paslaugos, 0%" },
    { value: "PVM39", label: "PVM39 - Iš užsienio valstybių paslaugos, neapmokestinama" },
    { value: "PVM21", label: "PVM21 - Iš ES PVM mokėtojų paslaugos, 21%" },
    { value: "PVM40", label: "PVM40 - Iš ES PVM mokėtojų paslaugos, 5%" },
    { value: "PVM41", label: "PVM41 - Iš ES PVM mokėtojų paslaugos, 0%" },
    { value: "PVM42", label: "PVM42 - Iš ES PVM mokėtojų paslaugos, neapmokestinama" },
    { value: "PVM43", label: "PVM43 - Užsienio apmokestinamojo asmens prekės, 21%" },
    { value: "PVM44", label: "PVM44 - Užsienio apmokestinamojo asmens prekės, 9%" },
    { value: "PVM45", label: "PVM45 - Užsienio apmokestinamojo asmens prekės, 5%" },
    { value: "PVM46", label: "PVM46 - Užsienio apmokestinamojo asmens prekės, 0%" },
    { value: "PVM47", label: "PVM47 - Užsienio apmokestinamojo asmens prekės, neapmokestinama" },
    {
      value: "PVM48",
      label: "PVM48 - Už Lietuvos ribų įsigytos prekės ir/ar paslaugos, neapmokestinama",
    },
    { value: "PVM49", label: "PVM49 - Žemės ūkio produkcija iš ūkininkų, 6%" },
    { value: "PVM100", label: "PVM100 - Kiti atvejai" },
    { value: "PVM50", label: "PVM50 - ES PVM mokėtojams tiekiamos prekės, 0%" },
    { value: "PVM51", label: "PVM51 - PVM taikomi sandoriai, 0%" },
    { value: "PVM52", label: "PVM52 - Sandoriai su specialia schema (marža), 9%" },
    { value: "PVM53", label: "PVM53 - Sandoriai su specialia schema (marža), 5%" },
    { value: "PVM54", label: "PVM54 - Iš užsienio paslaugos, 9%" },
    { value: "PVM55", label: "PVM55 - Paramos gavėjams skirtos prekės ir/ar paslaugos, 0%" },
    {
      value: "PVM56",
      label: "PVM56 - Paramos gavėjams iš ES valstybės narės įsigytos prekės, neapmokestinama",
    },
    { value: "PVM57", label: "PVM57 - Iš ES PVM mokėtojų paslaugos, 9%" },
    { value: "NE_ISAF", label: "NE_ISAF - Nepatenka į I.SAF ataskaitą" },
  ];

  // Funkcija atidaryti PDF tipo pasirinkimo dialogą
  const openPdfTypeDialog = () => {
    if (!selectedCompany) {
      showSnackbar(t("selectCompanyFirst"), "error");
      return;
    }

    if (!hasActiveFilters()) {
      showSnackbar(t("applyFilters"), "warning");
      return;
    }

    setPdfDialogOpen(true);
  };

  // PDF tipo dialogo uždarymas
  const handlePdfDialogClose = () => {
    setPdfDialogOpen(false);
    setUnitPriceInput(""); // Reset unit price when closing dialog
  };

  // PDF tipo pasirinkimas ir generavimas
  const handlePdfTypeSelect = () => {
    // Validate unit price if provided
    if (unitPriceInput && (isNaN(parseFloat(unitPriceInput)) || parseFloat(unitPriceInput) <= 0)) {
      showSnackbar("Please enter a valid unit price", "error");
      return;
    }

    setPdfDialogOpen(false);
    generatePdfMutation.mutate({
      documentType,
      unitPrice: unitPriceInput ? parseFloat(unitPriceInput) : null,
    });
  };

  // Function to check if at least one filter is active
  const hasActiveFilters = () => {
    return (
      filters.suppliers.length > 0 ||
      (Array.isArray(filters.documentTypes) && filters.documentTypes.length > 0) ||
      filters.startDate !== null ||
      filters.endDate !== null ||
      filters.xmlType !== "" ||
      filters.searchQuery !== ""
    );
  };

  // Handle search
  const handleSearch = (searchData) => {
    // Handle both old string format and new object format for backward compatibility
    if (typeof searchData === "string") {
      setFilters((prev) => ({
        ...prev,
        searchQuery: searchData,
        searchField: "all",
        exactMatch: false,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        searchQuery: searchData.query,
        searchField: searchData.field,
        exactMatch: searchData.exactMatch || false,
      }));
    }
    setPageIndex(0); // Reset to first page when searching
  };

  // Dokumentų krovimas su paginacija tik tada, kai yra filtrai
  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = useQuery({
    queryKey: ["documents", selectedCompany?.id, filters, pageIndex, pageSizeValue],
    queryFn: async () => {
      const params = {
        company_id: selectedCompany?.id,
        suppliers:
          filters.suppliers.length > 0
            ? filters.suppliers.map((s) => s.supplier_registration).join(",")
            : undefined,
        exclude_suppliers: filters.excludeSuppliers,
        date_type: filters.dateType || "invoice",
        start_date: filters.startDate ? dayjs(filters.startDate).format("YYYY-MM-DD") : undefined,
        end_date: filters.endDate ? dayjs(filters.endDate).format("YYYY-MM-DD") : undefined,
        xml_type: filters.xmlType || undefined,
        document_types:
          Array.isArray(filters.documentTypes) && filters.documentTypes.length > 0
            ? filters.documentTypes.join(",")
            : undefined,
        search: filters.searchQuery || undefined,
        search_field: filters.searchField || "all",
        exact_match: filters.exactMatch || false,
        exclude_document_types: filters.excludeDocumentTypes,
        page: pageIndex + 1, // Backend uses 1-indexed pages
        page_size: pageSizeValue, // IMPORTANT: Set page size to 200 explicitly
      };

      console.log("Fetching documents with params:", params);

      try {
        const response = await api.get(`/api/documents/`, { params });
        console.log("API Response:", {
          totalCount: response.data.total_count,
          totalPages: response.data.total_pages,
          currentPage: response.data.current_page,
          documentsCount: response.data.documents?.length || 0,
        });

        setTotalCount(response.data.total_count);
        setTotalPages(response.data.total_pages);
        return response.data.documents || [];
      } catch (error) {
        console.error("API Error:", error);
        throw error;
      }
    },
    enabled: !!selectedCompany?.id && hasActiveFilters(), // Leidžia krauti tik jei yra filtrai
    staleTime: 0, // Always consider data stale
    cacheTime: 0, // Disable caching
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Always refetch on window focus
    select: (data) =>
      data.map((doc) => {
        const { id: dataId, ...dataWithoutId } = doc.data || {};
        return {
          ...doc,
          ...dataWithoutId,
          is_recognized: doc.is_recognized === true,
          isValid: doc.data ? validateTotals({ ...doc.data, line_items: doc.line_items }) : false,
        };
      }),
  });

  const recognizeMutation = useMutation({
    mutationFn: async ({ document_ids, use_company_code, use_markdown }) => {
      console.log(
        "Recognizing with IDs:",
        document_ids,
        "Use company code:",
        use_company_code,
        "Use markdown:",
        use_markdown
      );
      const response = await api.post(`/api/recognize/`, {
        document_ids,
        use_company_code: use_company_code,
        use_markdown: use_markdown,
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Recognition success:", data);
      queryClient.invalidateQueries(["documents", selectedCompany?.id, filters]);
      showSnackbar(data.message || t("recognitionCompleted"), "success");
      toggleAllRowsSelected(false);
    },
    onError: (error) => {
      console.error("Recognition error:", error.response?.data || error.message);
      // 504 from nginx or gateway: show localized timeout info
      if (error.response?.status === 504) {
        showSnackbar(
          t("errorRecognizingDocument") +
            (t("gatewayTimeoutSuffix") || ": serverio laiko limitas (504)"),
          "error"
        );
        return;
      }
      if (error.response?.status === 400) {
        if (
          error.response?.data?.message?.includes("No LLM settings configured") ||
          error.response?.data?.message?.includes("No model version specified")
        ) {
          showSnackbar(
            "Please configure LLM settings with valid model versions before recognizing documents.",
            "error"
          );
        } else if (error.response?.data?.message?.includes("Invalid")) {
          showSnackbar(
            "Invalid model version in LLM settings. Please update your model version in the settings page.",
            "error"
          );
        } else {
          showSnackbar(error.response?.data?.message || t("errorRecognizingDocument"), "error");
        }
      } else {
        showSnackbar(error.response?.data?.message || t("errorRecognizingDocument"), "error");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (document_ids) => {
      console.log("Deleting document IDs:", document_ids);
      const response = await api.post(`/api/documents/delete-multiple/`, { document_ids });
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Delete success:", data);
      queryClient.invalidateQueries(["documents", selectedCompany?.id, filters]);
      // Normalize server message like "1 documents have been deleted."
      let message = data.message;
      if (typeof message === "string") {
        const m = message.match(/(\d+)\s+documents?\s+have\s+been\s+deleted\.?/i);
        if (m) {
          const count = m[1];
          message = (t("documentsDeletedCount") || "{count} documents have been deleted.").replace(
            "{count}",
            count
          );
        }
      }
      showSnackbar(message || t("documentsDeletedSuccessfully"), "success");
      toggleAllRowsSelected(false);
    },
    onError: (error) => {
      console.error("Delete error:", error.response?.data || error.message);
      showSnackbar(error.response?.data?.message || "Failed to delete documents", "error");
    },
  });

  // Helper function to poll for async export task completion
  const pollExportTask = async (taskId, onProgress) => {
    // Backend keeps async export tasks around for ~30 minutes (see xml_generator/async_export.py).
    // LLM-based exports can realistically take >10 minutes, so keep polling longer to avoid
    // premature "Export timed out" errors in the UI.
    const maxAttempts = 360; // 30 minutes max (5s interval)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await api.get(`/api/export_task_status/${taskId}/`);
        const { status, progress, message, result, error } = response.data;

        if (onProgress) {
          onProgress({ status, progress, message });
        }

        if (status === "completed") {
          return result;
        } else if (status === "failed") {
          throw new Error(error || "Export failed");
        }

        // Wait 5 seconds before next poll
        await new Promise((resolve) => setTimeout(resolve, 5000));
        attempts++;
      } catch (err) {
        if (err.response?.status === 404) {
          throw new Error("Export task not found");
        }
        throw err;
      }
    }

    throw new Error("Export timed out");
  };

  const exportXmlMutation = useMutation({
    mutationFn: async ({
      exportType,
      document_ids,
      use_matches,
      pozymiai,
      include_pvm_in_total,
      spacy_model_size,
      override_pvm_code,
      recalculate_vat,
      matcher_type,
      onProgress,
    }) => {
      const endpoint =
        exportType === "agnum_summary" ? "/api/export_summary_xml/" : "/api/export_xml/";
      const response = await api.post(endpoint, {
        export_type: exportType,
        document_ids,
        use_matches,
        pozymiai: pozymiai || [],
        include_pvm_in_total: include_pvm_in_total !== undefined ? include_pvm_in_total : true,
        spacy_model_size: spacy_model_size || "md",
        override_pvm_code: override_pvm_code || "",
        recalculate_vat: !!recalculate_vat,
        matcher_type: matcher_type || "llm",
        async: true, // Request async processing
      });

      // Check if server returned async task
      if (response.data.async && response.data.task_id) {
        // Poll for completion
        if (onProgress) {
          onProgress({ status: "processing", progress: 0, message: "Export started..." });
        }
        return await pollExportTask(response.data.task_id, onProgress);
      }

      // Sync response (for small exports or if async disabled)
      return response.data;
    },
    onSuccess: (data) => {
      if (data.xml_content && data.filename) {
        const blob = new Blob([data.xml_content], { type: "application/xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        showSnackbar(t("xmlExportedSuccessfully"), "success");
      } else {
        showSnackbar("Failed to export XML", "error");
      }
    },
    onError: (error) => {
      console.error("Export XML error:", error);
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || error.message;
      const details = error.response?.data?.details;
      if (details) {
        showSnackbar(
          `Export failed: ${errorMessage}. Problem with document ${details.invoice_id} from ${details.supplier_name}`,
          "error"
        );
      } else {
        showSnackbar(`Export failed: ${errorMessage}`, "error");
      }
    },
  });

  const exportExcelMutation = useMutation({
    mutationFn: async ({ document_ids, use_matches, spacy_model_size }) => {
      const response = await api.post(
        "/api/export_excel/",
        {
          document_ids,
          use_matches,
          spacy_model_size: spacy_model_size || "md",
        },
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      // Build filename identical to XML export: use actual invoice_date range of exported documents
      const selectedIds =
        selectedFlatRows.length > 0
          ? selectedFlatRows.map((row) => row.original.id)
          : (documents || []).map((doc) => doc.id);

      const selectedDocs = (documents || []).filter((d) => selectedIds.includes(d.id));
      const invoiceDates = selectedDocs
        .map((d) => d.invoice_date)
        .filter(Boolean)
        .map((d) => dayjs(d))
        .filter((d) => d.isValid());

      let filename;
      if (invoiceDates.length > 0) {
        const minObj = invoiceDates.reduce((acc, d) => (acc && acc.isBefore(d) ? acc : d));
        const maxObj = invoiceDates.reduce((acc, d) => (acc && acc.isAfter(d) ? acc : d));
        const minDate = minObj.format("YYYY-MM-DD");
        const maxDate = maxObj.format("YYYY-MM-DD");
        // Always use _to_ pattern to match XML naming, even if equal
        filename = `documents_${minDate}_to_${maxDate}.xlsx`;
      } else {
        // Match XML fallback when no invoice dates are present
        filename = `documents_unknown_start_to_unknown_end.xlsx`;
      }

      // Create download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      showSnackbar("Excel failas sėkmingai eksportuotas", "success");
    },
    onError: (error) => {
      console.error("Export Excel error:", error);
      const errorMessage = error.response?.data?.error || error.message;
      showSnackbar(`Failed to export Excel: ${errorMessage}`, "error");
    },
  });

  // RODA TXT export mutation
  const exportRodaMutation = useMutation({
    mutationFn: async ({ document_ids, include_unit }) => {
      const response = await api.post(
        "/api/export_roda_txt/",
        { document_ids, include_unit: include_unit !== false },
        { responseType: "blob" }
      );
      return response;
    },
    onSuccess: (response) => {
      // Build filename from backend headers if present, otherwise fallback
      const disposition =
        response.headers?.["content-disposition"] || response.headers?.get?.("content-disposition");
      let filename = "roda_eilutes.txt";
      if (disposition) {
        const m = /filename="?([^";]+)"?/i.exec(disposition);
        if (m && m[1]) filename = m[1];
      }

      const contentType = response.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      showSnackbar("RODA TXT sėkmingai eksportuotas", "success");
    },
    onError: (error) => {
      console.error("Export RODA error:", error);
      const errorMessage = error.response?.data?.error || error.message;
      showSnackbar(`Failed to export RODA TXT: ${errorMessage}`, "error");
    },
  });

  const handleExportRoda = () => {
    if (!documents || documents.length === 0) {
      showSnackbar("Nėra dokumentų eksportui", "error");
      return;
    }
    const selectedIds =
      selectedFlatRows.length > 0
        ? selectedFlatRows.map((row) => row.original.id)
        : documents.map((doc) => doc.id);
    const msg = `Eksportuojami ${selectedIds.length} dokumentai į RODA TXT...`;
    showSnackbar(msg, "info");
    exportRodaMutation.mutate({ document_ids: selectedIds, include_unit: true });
  };

  const generatePdfMutation = useMutation({
    mutationFn: async ({ documentType, unitPrice }) => {
      if (!selectedCompany?.id) {
        throw new Error("Company not selected"); // Prevent API call if no company
      }

      // Construct params from current filters
      const params = {
        company_id: selectedCompany.id,
        suppliers:
          filters.suppliers.length > 0
            ? filters.suppliers.map((s) => s.supplier_registration).join(",")
            : undefined,
        date_type: filters.dateType || "invoice",
        start_date: filters.startDate ? dayjs(filters.startDate).format("YYYY-MM-DD") : undefined,
        end_date: filters.endDate ? dayjs(filters.endDate).format("YYYY-MM-DD") : undefined,
        xml_type: filters.xmlType || undefined,
        search: filters.searchQuery || undefined,
        search_field: filters.searchField || "all",
        exact_match: filters.exactMatch || false,
        document_type: documentType, // Add document type parameter
        unit_price: unitPrice || undefined, // Add unit price parameter
      };

      console.log("Generating PDF report with params:", params);

      // Call the backend endpoint to generate the PDF report
      const response = await api.get(`/api/documents/generate-pdf-report/`, {
        params,
        responseType: "blob", // Expect a blob response
      });

      return response.data; // Return the blob data
    },
    onSuccess: (data) => {
      console.log("PDF generated successfully, creating download link");

      // Create a blob URL from the response data
      const pdfBlob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;

      // Create a dynamic filename or use a default
      const filename = `report_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
      a.download = filename;

      document.body.appendChild(a); // Append the link to the body
      a.click(); // Programmatically click the link to trigger download
      window.URL.revokeObjectURL(url); // Clean up the blob URL
      a.remove(); // Remove the link element

      showSnackbar(t("pdfGeneratedSuccessfully"), "success");
    },
    onError: (error) => {
      console.error("PDF Generation error:", error);

      // Try to extract error message from response
      let errorMessage = "Failed to generate PDF report";

      if (error.message === "Company not selected") {
        errorMessage = t("selectCompanyFirst");
      } else if (error.response?.data) {
        try {
          // For JSON responses
          if (typeof error.response.data === "object") {
            errorMessage = error.response.data.error || error.response.data.message || errorMessage;
          }
          // For blob responses with error
          else if (error.response.data instanceof Blob) {
            // We can't directly read the Blob here, so we use a generic message
            errorMessage = "Server returned an error. Check your filters and try again.";
          }
        } catch (e) {
          console.error("Error parsing error response:", e);
        }
      }

      showSnackbar(errorMessage, "error");
    },
  });

  // Explicitly define handleGeneratePdfReport function
  const handleGeneratePdfReport = () => {
    console.log("handleGeneratePdfReport called");
    openPdfTypeDialog();
  };

  const handleRecognize = (selectedDocumentIds) => {
    if (selectedDocumentIds.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }

    if (!selectedCompany?.id) {
      showSnackbar(t("selectCompanyFirst"), "error");
      return;
    }

    // Open dialog to choose recognition options instead of immediately recognizing
    setDocumentsToRecognize(selectedDocumentIds);
    setRecognitionDialogOpen(true);
  };

  const handleRecognitionOptionsConfirm = () => {
    setRecognitionDialogOpen(false);
    // Run sequential per-document recognition to surface progress in UI and avoid timeouts
    const ids = Array.isArray(documentsToRecognize) ? [...documentsToRecognize] : [];
    if (ids.length === 0) return;

    const markProcessing = (id, on = true) => {
      if (!isMountedRef.current) return;
      setProcessingIds((prev) => {
        const next = new Set(prev);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      });
    };

    const scrollRowIntoView = (id) => {
      // Defer until row renders the processing state
      setTimeout(() => {
        const rowEl = document.querySelector(`tr[data-doc-id="${id}"]`);
        if (rowEl && typeof rowEl.scrollIntoView === "function") {
          rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
    };

    (async () => {
      // Show a single toast at the beginning of the batch
      const startMsgTemplate =
        t("processingBatchStart") || "Pradedamas {n} dokumentų atpažinimas...";
      showSnackbar(startMsgTemplate.replace("{n}", String(ids.length)), "info");
      setIsBatchRunning(true);

      let successCount = 0;
      let failCount = 0;
      for (let i = 0; i < ids.length; i++) {
        if (!isMountedRef.current) break;
        if (isCancelling) break; // local immediate stop
        const id = ids[i];
        markProcessing(id, true);
        scrollRowIntoView(id);
        try {
          await api.post(
            `/api/recognize/`,
            {
              document_ids: [id],
              use_company_code: useCompanyCode,
              use_markdown: useMarkdown,
            },
            { timeout: 300000 }
          );
          successCount++;
          // Fetch fresh document and merge into the current list cache for immediate UI update
          try {
            const detailResp = await api.get(`/api/documents/${id}/`);
            const fresh = detailResp.data;
            const { id: dataId, ...dataWithoutId } = fresh.data || {};
            const mergedDoc = {
              ...fresh,
              ...dataWithoutId,
              is_recognized: fresh.is_recognized === true,
              isValid: fresh.data
                ? validateTotals({ ...fresh.data, line_items: fresh.line_items })
                : false,
            };
            // Update local overlay map for immediate UI without touching React Query cache
            setDocOverlays((prev) => {
              const next = new Map(prev);
              next.set(id, mergedDoc);
              return next;
            });
          } catch (e) {
            // Non-fatal: if detail fetch/cache merge fails, final invalidate will refresh
          }
        } catch (error) {
          failCount++;
          // continue to next id
        } finally {
          markProcessing(id, false);
          // Do not refetch here to avoid heavy re-rendering; we'll do a final refresh
        }
      }

      // Final summary
      const summaryTemplate =
        t("recognitionSummary") || "Atpažinimas baigtas. Sėkmingai: {ok}, Nesėkmingai: {fail}.";
      showSnackbar(
        summaryTemplate.replace("{ok}", String(successCount)).replace("{fail}", String(failCount)),
        failCount > 0 ? "warning" : "success"
      );
      toggleAllRowsSelected(false);
      if (isMountedRef.current) setIsBatchRunning(false);
      // Skip heavy final refetch to avoid churn; per-document merges already updated the UI
    })();
  };

  const handleCancelRecognition = async () => {
    try {
      setIsCancelling(true);
      await api.post(`/api/cancel_all_recognitions/`);
      showSnackbar(t("recognitionCancelRequested") || "Recognition cancel requested", "info");
    } catch (e) {
      console.error("Cancel recognition error:", e);
      showSnackbar(t("recognitionCancelFailed") || "Failed to request cancel", "error");
    } finally {
      // Allow new batches later
      setTimeout(() => setIsCancelling(false), 1000);
    }
  };

  const handleDeleteSelected = (selectedDocumentIds) => {
    if (selectedDocumentIds.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }
    // Ask for confirmation before deleting
    const msgTemplate = t("deleteSelectedConfirm");
    const msg =
      msgTemplate && msgTemplate.includes("{count}")
        ? msgTemplate.replace("{count}", selectedDocumentIds.length)
        : `${t("areYouSure")} ${selectedDocumentIds.length} ${t("documents").toLowerCase()}? ${t(
            "thisActionCannotBeUndone"
          )}`;
    if (!window.confirm(msg)) return;
    deleteMutation.mutate(selectedDocumentIds);
  };

  const handleExportXml = (exportType = "agnum", exportWithMatches = false) => {
    // Instead of directly exporting, open the settings dialog
    setXmlExportType(exportType);
    setXmlExportWithMatches(exportWithMatches);
    openXmlExportDialog();
  };

  const handleExportExcel = () => {
    if (!documents || documents.length === 0) {
      showSnackbar("Nėra dokumentų eksportui", "error");
      return;
    }

    const selectedIds =
      selectedFlatRows.length > 0
        ? selectedFlatRows.map((row) => row.original.id)
        : documents.map((doc) => doc.id);

    const exportingExcelMsg = t("exportingExcel")
      ? t("exportingExcel").replace("{count}", selectedIds.length)
      : `Eksportuojami ${selectedIds.length} dokumentai į Excel...`;
    showSnackbar(exportingExcelMsg, "info");

    exportExcelMutation.mutate({
      document_ids: selectedIds,
      use_matches: false, // Default to NOT using matches for Excel export (can be changed to true if needed)
      spacy_model_size: "md",
    });
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!selectedCompany) {
      showSnackbar(t("selectCompanyFirst"), "error");
      return;
    }
    const formData = new FormData();
    formData.append("company_id", selectedCompany.id);
    files.forEach((file) => {
      formData.append("files", file);
    });
    try {
      const response = await api.post(`/api/upload/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // Increased to 5 minutes for multiple document processing
      });
      if (response.data.success) {
        // Backend may include a summary like: "Successfully uploaded X documents. Failed to upload Y documents."
        const msg = response.data.message;
        if (
          typeof msg === "string" &&
          msg.includes("Successfully uploaded") &&
          msg.includes("Failed to upload")
        ) {
          const match = msg.match(
            /Successfully uploaded\s+(\d+)\s+documents?\.\s+Failed to upload\s+(\d+)\s+documents?\./i
          );
          if (match) {
            const [, uploaded, failed] = match;
            const localized = (
              t("uploadSummary") ||
              "Successfully uploaded {uploaded} documents. Failed to upload {failed} documents."
            )
              .replace("{uploaded}", uploaded)
              .replace("{failed}", failed);
            showSnackbar(localized, failed === "0" ? "success" : "warning");
          } else {
            showSnackbar(t("filesUploadedSuccessfully"), "success");
          }
        } else {
          // Fallback to backend message or localized success
          showSnackbar(msg || t("filesUploadedSuccessfully"), "success");
        }
      } else {
        showSnackbar(
          response.data.message || t("failedToUploadDocuments") || "Failed to upload documents",
          "error"
        );
      }
    } catch (error) {
      // Don't show error if it's a 500 error but still refresh the documents list
      if (error.response && error.response.status === 500) {
        showSnackbar(t("filesUploadedSuccessfully"), "success");
        // Don't log the error for 500 status
      } else {
        showSnackbar(
          t("errorUploadingFiles") || `Error uploading files: ${error.message}`,
          "error"
        );
        console.error("Upload error:", error);
      }
    } finally {
      // Always refresh the document list regardless of whether there was an error
      queryClient.invalidateQueries(["documents", selectedCompany?.id, filters]);
      // Reset the file input value to allow uploading the same file again
      event.target.value = null;
    }
  };

  const handleRecognizeUnrecognized = () => {
    const unrecognizedDocuments = documents.filter((doc) => !doc.is_recognized);
    const selectedDocumentIds = unrecognizedDocuments.map((doc) => doc.id);
    if (selectedDocumentIds.length === 0) {
      showSnackbar("No unrecognized documents found for the selected company.", "info");
      return;
    }
    // Use the dialog approach instead of directly recognizing
    setDocumentsToRecognize(selectedDocumentIds);
    setRecognitionDialogOpen(true);
  };

  const handleRecognizeHeaders = () => {
    const unrecognizedDocuments = documents.filter((doc) => !doc.is_recognized);
    const selectedDocumentIds = unrecognizedDocuments.map((doc) => doc.id);
    if (selectedDocumentIds.length === 0) {
      showSnackbar("No unrecognized documents found for the selected company.", "info");
      return;
    }
    // Use the dialog approach instead of directly recognizing
    setDocumentsToRecognize(selectedDocumentIds);
    setRecognitionDialogOpen(true);
  };

  const columns = useMemo(() => {
    return documentTableData(t).columns;
  }, [t]);

  // Always provide an array to react-table and merge local overlays for immediate row updates
  const data = useMemo(() => {
    const base = Array.isArray(documents) ? documents : [];
    if (!docOverlays || docOverlays.size === 0) return base;
    return base.map((doc) => docOverlays.get(doc.id) || doc);
  }, [documents, docOverlays]);

  // Options for "per page" selector, responsive to screen size
  const perPageOptions = useMemo(
    () => (isMdDown ? [20, 50, 100] : [50, 100, 200, 500]),
    [isMdDown]
  );

  // Header checkbox to select all rows on the current page
  const SelectionHeader = ({ getToggleAllPageRowsSelectedProps }) => {
    return <Checkbox {...getToggleAllPageRowsSelectedProps()} color="primary" />;
  };

  SelectionHeader.propTypes = {
    getToggleAllPageRowsSelectedProps: PropTypes.func.isRequired,
  };

  const SelectionCell = ({ row }) => {
    return <Checkbox {...row.getToggleRowSelectedProps()} color="primary" />;
  };

  SelectionCell.propTypes = {
    row: PropTypes.shape({
      getToggleRowSelectedProps: PropTypes.func.isRequired,
    }).isRequired,
  };

  // Processing spinner cell with PropTypes to satisfy eslint
  const ProcessingCell = ({ row }) => {
    return processingIds.has(row.original.id) ? (
      <MDBox display="flex" alignItems="center" justifyContent="center" px={0.5}>
        <CircularProgress size={18} thickness={5} color="primary" />
      </MDBox>
    ) : null;
  };

  ProcessingCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired,
    }).isRequired,
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { pageSize, pageIndex: tablePageIndex, selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: isMdDown ? 50 : 200,
        pageIndex: pageIndex,
      },
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Per-row processing indicator
        {
          id: "processing",
          Header: () => null,
          Cell: ProcessingCell,
          width: 44,
        },
        {
          id: "selection",
          Header: SelectionHeader,
          Cell: SelectionCell,
        },
        ...columns,
      ]);
    }
  );

  // Keep react-table's internal pageIndex in sync with our external pageIndex
  useEffect(() => {
    if (tablePageIndex !== pageIndex) {
      gotoPage(pageIndex);
    }
  }, [pageIndex, tablePageIndex, gotoPage]);

  const handleResetFilters = () => {
    setFilters({
      suppliers: [],
      excludeSuppliers: false,
      documentTypes: [],
      excludeDocumentTypes: false,
      dateType: "invoice",
      startDate: null,
      endDate: null,
      xmlType: "",
      searchQuery: "",
      searchField: "all",
      exactMatch: false,
    });
  };

  useEffect(() => {
    if (selectedCompany) {
      handleResetFilters();
    }
  }, [selectedCompany]);

  // Adapt UI defaults for mobile/desktop
  useEffect(() => {
    setFiltersExpanded(!isMdDown);
  }, [isMdDown]);

  useEffect(() => {
    // Keep logical page size comfortable for the screen size
    const target = isMdDown ? 50 : 200;
    if (pageSizeValue !== target) {
      setPageSizeValue(target);
      setPageSize(target);
      setPageIndex(0);
    }
  }, [isMdDown, pageSizeValue, setPageSize, setPageIndex]);

  // Guard: if totalPages shrinks (e.g., after recognition/filter change), keep pageIndex in range
  useEffect(() => {
    if (typeof totalPages === "number" && totalPages > 0 && pageIndex >= totalPages) {
      setPageIndex(Math.max(0, totalPages - 1));
    }
  }, [totalPages, pageIndex]);

  // If current page becomes empty while there are items overall, jump to last valid page
  useEffect(() => {
    if (!documents) return;
    const overall = documents.length;
    const currentCount = page?.length ?? 0;
    if (overall > 0 && currentCount === 0 && totalPages > 0) {
      setPageIndex(Math.max(0, Math.min(pageIndex, totalPages - 1)));
    }
  }, [documents, page?.length, totalPages]);

  const selectedDocumentIds = useMemo(
    () => selectedFlatRows.map((row) => row.original.id),
    [selectedFlatRows]
  );

  const handlePageChangeInternal = (event, value) => {
    setPageIndex(value - 1);
  };

  const handleDocumentsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSizeValue(newSize);
    setPageIndex(0); // Reset to first page when changing page size
    console.log(`Changed page size to ${newSize}`);
    // Keep react-table page size in sync with selection
    setPageSize(newSize);
  };

  // XML Export dialog handlers
  const openXmlExportDialog = () => {
    if (selectedFlatRows.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }
    setXmlExportDialogOpen(true);
  };

  const handleXmlExportDialogClose = () => {
    setXmlExportDialogOpen(false);
    setSelectedPozymiai([]);
    setIncludePvmInTotal(true);
    setXmlExportType("agnum");
    setXmlExportWithMatches(false);
    setSpacyModelSize("md");
    setMatcherType("llm");
    setOverridePvmCode("");
    setRecalculateVat(false);
  };

  const handlePozymiaiToggle = (pozymiusId) => {
    setSelectedPozymiai((prev) =>
      prev.includes(pozymiusId) ? prev.filter((id) => id !== pozymiusId) : [...prev, pozymiusId]
    );
  };

  // Details dialog handlers
  const openDetailsDialog = (doc) => {
    setSelectedDocument(doc);
    setDetailsDialogOpen(true);
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleXmlExportConfirm = () => {
    setXmlExportDialogOpen(false);

    // Get all document IDs from selectedFlatRows
    const documentIds = selectedFlatRows.map((row) => row.original.id);

    console.log(`Exporting ${documentIds.length} documents with settings:`, {
      exportType: xmlExportType,
      withMatches: xmlExportWithMatches,
      pozymiai: selectedPozymiai,
      includePvmInTotal: includePvmInTotal,
      spacyModelSize: spacyModelSize,
      matcherType: matcherType,
      overridePvmCode: overridePvmCode,
    });

    const exportingMsg = t("exportingXml").replace("{count}", documentIds.length);
    showSnackbar(exportingMsg, "info");

    exportXmlMutation.mutate({
      exportType: xmlExportType,
      document_ids: documentIds,
      use_matches: xmlExportWithMatches,
      matcher_type: matcherType,
      pozymiai: selectedPozymiai,
      include_pvm_in_total: includePvmInTotal,
      spacy_model_size: spacyModelSize,
      matcher_type: matcherType,
      override_pvm_code: overridePvmCode,
      recalculate_vat: recalculateVat,
    });
  };

  // Keep react-table page size synced when pageSizeValue changes (e.g., selecting 500)
  useEffect(() => {
    setPageSize(pageSizeValue);
  }, [pageSizeValue, setPageSize]);

  // Reset overlays when the dataset context changes
  useEffect(() => {
    setDocOverlays(new Map());
  }, [selectedCompany?.id, filters, pageIndex, pageSizeValue]);

  // Hydrate filters and pagination from sessionStorage when opening the page (per company)
  useEffect(() => {
    if (!selectedCompany?.id) return;
    if (didHydrateRef.current) return;
    try {
      const raw = sessionStorage.getItem(`documentsPage:${selectedCompany.id}`);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          if (saved.filters) setFilters(saved.filters);
          if (typeof saved.pageIndex === "number") setPageIndex(saved.pageIndex);
          if (typeof saved.pageSizeValue === "number") {
            setPageSizeValue(saved.pageSizeValue);
            setPageSize(saved.pageSizeValue);
          }
          if (typeof saved.filtersExpanded === "boolean") setFiltersExpanded(saved.filtersExpanded);
        }
      }
    } catch (e) {
      console.error("Failed to hydrate documents page state:", e);
    } finally {
      didHydrateRef.current = true;
    }
  }, [selectedCompany?.id, setPageSize]);

  // Persist filters and pagination to sessionStorage on change (per company)
  useEffect(() => {
    if (!selectedCompany?.id) return;
    const toSave = {
      filters,
      pageIndex,
      pageSizeValue,
      filtersExpanded,
    };
    try {
      sessionStorage.setItem(`documentsPage:${selectedCompany.id}`, JSON.stringify(toSave));
    } catch (e) {
      // Non-fatal
      console.warn("Failed to save documents page state:", e);
    }
  }, [selectedCompany?.id, filters, pageIndex, pageSizeValue, filtersExpanded]);

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("documentsManagement")}
                </MDTypography>
              </MDBox>
              <MDBox p={3} sx={{ overflowX: "auto" }}>
                {/* Sticky Cancel button shown during batch processing */}
                {isBatchRunning && (
                  <MDBox
                    sx={{
                      position: "sticky",
                      top: 8,
                      zIndex: 10,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <MDButton
                      color="error"
                      variant="contained"
                      onClick={handleCancelRecognition}
                      disabled={isCancelling}
                    >
                      {isCancelling
                        ? t("cancelling") || "Cancelling..."
                        : t("cancelRecognition") || "Cancel recognition"}
                    </MDButton>
                  </MDBox>
                )}
                <Grid container spacing={2}>
                  {isMdDown ? (
                    <Grid item xs={12}>
                      <Accordion
                        elevation={0}
                        expanded={filtersExpanded}
                        onChange={() => setFiltersExpanded((p) => !p)}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <MDTypography variant="button" fontWeight="medium">
                            {t("searchAndFilters") || "Search & Filters"}
                          </MDTypography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <SearchBar
                                onSearch={handleSearch}
                                initialQuery={filters.searchQuery}
                                initialField={filters.searchField}
                                initialExact={filters.exactMatch}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FilterBar
                                filters={filters}
                                setFilters={setFilters}
                                documentCount={totalCount}
                              />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <SearchBar
                          onSearch={handleSearch}
                          initialQuery={filters.searchQuery}
                          initialField={filters.searchField}
                          initialExact={filters.exactMatch}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FilterBar
                          filters={filters}
                          setFilters={setFilters}
                          documentCount={totalCount}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                      sx={{
                        backgroundColor: "background.paper",
                        p: 2,
                        borderRadius: 1,
                        boxShadow: 1,
                        overflowX: "auto",
                      }}
                    >
                      <ActionButtons
                        onRecognize={() => handleRecognize(selectedDocumentIds)}
                        onRecognizeUnrecognized={() => handleRecognizeUnrecognized()}
                        onRecognizeHeaders={() => handleRecognizeHeaders()}
                        onDeleteSelected={() => handleDeleteSelected(selectedDocumentIds)}
                        onExportXml={handleExportXml}
                        onExportExcel={handleExportExcel}
                        onExportRoda={handleExportRoda}
                        onUpload={handleUpload}
                        onGeneratePdfReport={handleGeneratePdfReport}
                        isLoading={generatePdfMutation.isLoading}
                      />
                    </MDBox>
                  </Grid>
                </Grid>
                {documentsLoading ? (
                  <Loader message={t("loading")} />
                ) : documentsError ? (
                  <div style={{ color: "red", textAlign: "center", padding: "20px" }}>
                    {documentsError.message || "Error loading documents"}
                  </div>
                ) : !hasActiveFilters() ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>{t("applyFilters")}</div>
                ) : documents && documents.length > 0 ? (
                  <>
                    {isMdDown ? (
                      // Mobile: card list view
                      <MDBox display="flex" flexDirection="column" gap={2}>
                        {page.map((row, rowIndex) => {
                          prepareRow(row);
                          const o = row.original || {};
                          const isSelected = row.isSelected;
                          const isValid = !!o.isValid;
                          const supplier =
                            o.supplier_name ||
                            o.supplier ||
                            o.supplier_title ||
                            o.supplierName ||
                            "";
                          const invoiceNo =
                            o.invoice_id || o.invoice_number || o.number || o.invoice || "";
                          const dateVal = o.invoice_date || o.date || o.document_date || null;
                          const total =
                            o.total || o.total_amount || o.total_sum || o.amount_total || null;
                          const docTypeMeta = getDocumentTypeMeta(o, t);

                          return (
                            <Card
                              key={o.id || rowIndex}
                              style={{
                                border: isSelected
                                  ? "2px solid rgba(25, 118, 210, 0.5)"
                                  : "1px solid #eee",
                                backgroundColor: isValid ? "#ffffff" : "#ffefef",
                                borderRadius: 8,
                                cursor: "pointer",
                              }}
                              onClick={() => o && openDetailsDialog(o)}
                            >
                              <MDBox p={2} display="flex" flexDirection="column" gap={1}>
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                >
                                  <MDTypography variant="button" fontWeight="medium">
                                    {supplier || t("unknownSupplier") || ""}
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    {docTypeMeta && (
                                      <Chip
                                        label={docTypeMeta.label}
                                        color={docTypeMeta.chipColor}
                                        variant={docTypeMeta.chipVariant}
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    )}
                                    {processingIds.has(o.id) && (
                                      <MDBox display="flex" alignItems="center" gap={0.5}>
                                        <CircularProgress size={18} thickness={5} />
                                        <MDTypography variant="caption" color="info">
                                          {t("processing") || "Processing..."}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                    {(() => {
                                      const toggleProps = row.getToggleRowSelectedProps();
                                      return (
                                        <Checkbox
                                          {...toggleProps}
                                          color="primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleProps.onClick && toggleProps.onClick(e);
                                          }}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleProps.onChange && toggleProps.onChange(e);
                                          }}
                                        />
                                      );
                                    })()}
                                  </MDBox>
                                </MDBox>
                                <MDTypography variant="caption" color="text">
                                  {t("invoice") || "Invoice"} #: {invoiceNo || "—"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {t("date") || "Date"}:{" "}
                                  {dateVal ? dayjs(dateVal).format("YYYY-MM-DD") : "—"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {t("total") || "Total"}: {total != null ? total : "—"}
                                </MDTypography>
                                {/* Removed totals status label for cleaner mobile card */}
                                {o.is_recognized === false && (
                                  <MDTypography variant="caption" color="warning">
                                    {t("notRecognized") || "Not recognized"}
                                  </MDTypography>
                                )}

                                <MDBox mt={1} display="flex" gap={1}>
                                  <MDButton
                                    size="small"
                                    color="info"
                                    variant="contained"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/documents/${o.id}`, {
                                        state: { from: { pathname: "/documents" } },
                                      });
                                    }}
                                  >
                                    {t("view") || "View"}
                                  </MDButton>
                                  <MDButton
                                    size="small"
                                    color="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDetailsDialog(o);
                                    }}
                                  >
                                    {t("details")}
                                  </MDButton>
                                </MDBox>
                              </MDBox>
                            </Card>
                          );
                        })}
                      </MDBox>
                    ) : (
                      // Desktop: table view
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          {headerGroups.map((headerGroup, index) => (
                            <tr
                              key={headerGroup.id || index}
                              style={{ backgroundColor: "#f5f5f5" }}
                            >
                              {headerGroup.headers.map((column, colIndex) => (
                                <th
                                  key={column.id || colIndex}
                                  onClick={() =>
                                    column.canSort && column.toggleSortBy(!column.isSortedDesc)
                                  }
                                  style={{
                                    borderBottom: "1px solid #ddd",
                                    padding: "8px",
                                    textAlign: "left",
                                    cursor: column.canSort ? "pointer" : "default",
                                    width: column.width ? `${column.width}px` : "auto",
                                  }}
                                >
                                  {column.render("Header")}
                                  {column.canSort && (
                                    <span>
                                      {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}
                                    </span>
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {page.map((row, rowIndex) => {
                            prepareRow(row);
                            const isSelected = row.isSelected;
                            const isProcessing = processingIds.has(row.original.id);
                            return (
                              <tr
                                key={row.original.id || rowIndex}
                                data-doc-id={row.original.id}
                                style={{
                                  backgroundColor: isProcessing
                                    ? "#e8f4ff"
                                    : row.original.isValid
                                    ? isSelected
                                      ? "#e0f7fa"
                                      : "white"
                                    : "#ffcccc",
                                  borderLeft: isProcessing ? "3px solid #1976d2" : undefined,
                                }}
                              >
                                {row.cells.map((cell, cellIndex) => (
                                  <td
                                    key={cell.column.id || cellIndex}
                                    style={{
                                      borderBottom: "1px solid #ddd",
                                      padding: "8px",
                                      width: cell.column.width ? `${cell.column.width}px` : "auto",
                                    }}
                                  >
                                    {cell.render("Cell")}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}

                    {/* Pagination Controls */}
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={3}
                      flexWrap="wrap"
                    >
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <MDTypography variant="body2" color="text">
                          Showing {page.length} of {totalCount} documents
                        </MDTypography>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>{t("perPage")}</InputLabel>
                          <Select
                            value={pageSizeValue}
                            onChange={handleDocumentsPerPageChange}
                            label={t("perPage")}
                          >
                            {perPageOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </MDBox>
                      <Pagination
                        count={totalPages}
                        page={pageIndex + 1}
                        onChange={handlePageChangeInternal}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </MDBox>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    {t("noDocumentsFound")}
                  </div>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Document Details Dialog (mobile) */}
      <Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} fullWidth maxWidth="md">
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <MDTypography variant="h6">{t("documentDetails") || "Document details"}</MDTypography>
          <IconButton aria-label="close" onClick={closeDetailsDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDocument ? (
            <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
              <MDBox>
                <MDTypography variant="button" fontWeight="medium">
                  {t("supplierName")}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {selectedDocument.supplier_name || selectedDocument.supplier || "—"}
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="button" fontWeight="medium">
                  {t("invoiceId")}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {selectedDocument.invoice_id || selectedDocument.invoice_number || "—"}
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="button" fontWeight="medium">
                  {t("invoiceDate")}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {selectedDocument.invoice_date
                    ? dayjs(selectedDocument.invoice_date).format("YYYY-MM-DD")
                    : "—"}
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="button" fontWeight="medium">
                  {t("totalAmount")}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {(() => {
                    const val =
                      selectedDocument.total_amount ??
                      selectedDocument.total ??
                      selectedDocument.total_sum ??
                      selectedDocument.amount_total ??
                      selectedDocument.grand_total ??
                      selectedDocument.data?.total ??
                      selectedDocument.data?.total_amount ??
                      null;
                    if (val == null) return "—";
                    const num = Number(val);
                    return Number.isFinite(num) ? num.toFixed(2) : String(val);
                  })()}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : (
            <Loader message={t("loading")} />
          )}
        </DialogContent>
        <DialogActions>
          {selectedDocument?.id && (
            <MDButton
              onClick={() =>
                navigate(`/documents/${selectedDocument.id}`, {
                  state: { from: { pathname: "/documents" } },
                })
              }
              color="info"
              variant="contained"
            >
              {t("openFullView")}
            </MDButton>
          )}
          <MDButton onClick={closeDetailsDialog} color="primary">
            {t("close") || "Close"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* PDF Type Selection Dialog */}
      <Dialog open={pdfDialogOpen} onClose={handlePdfDialogClose}>
        <DialogTitle>{t("selectPdfType")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("selectPdfTypeDescription")}</DialogContentText>
          <RadioGroup value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
            <FormControlLabel value="purchase" control={<Radio />} label={t("purchaseInvoices")} />
            <FormControlLabel value="sales" control={<Radio />} label={t("salesInvoices")} />
          </RadioGroup>

          <MDBox mt={3}>
            <TextField
              fullWidth
              label={t("unitPrice")}
              type="number"
              value={unitPriceInput}
              onChange={(e) => setUnitPriceInput(e.target.value)}
              helperText={t("unitPriceOptional")}
              InputProps={{
                inputProps: {
                  min: 0,
                  step: 0.01,
                },
              }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handlePdfDialogClose} color="secondary">
            {t("cancel")}
          </MDButton>
          <MDButton onClick={handlePdfTypeSelect} color="primary">
            {t("generate")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Recognition Options Dialog */}
      <Dialog open={recognitionDialogOpen} onClose={() => setRecognitionDialogOpen(false)}>
        <DialogTitle>{t("recognitionOptions")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("selectRecognitionOptions")}</DialogContentText>

          {/* Company Code Option */}
          <MDBox mt={2}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              {t("companyCodeOption")}
            </MDTypography>
            <RadioGroup
              value={useCompanyCode}
              onChange={(e) => setUseCompanyCode(e.target.value === "true")}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={t("useCompanyCodeFromSettings")}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label={t("extractCompanyCodeFromDocument")}
              />
            </RadioGroup>
          </MDBox>

          {/* Recognition Method Option */}
          <MDBox mt={3}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              {t("recognitionMethod")}
            </MDTypography>
            <RadioGroup
              value={useMarkdown}
              onChange={(e) => setUseMarkdown(e.target.value === "true")}
            >
              <FormControlLabel value={false} control={<Radio />} label={t("traditionalMethod")} />
              <FormControlLabel value={true} control={<Radio />} label={t("markdownMethod")} />
            </RadioGroup>
            <MDTypography variant="caption" color="text" mt={1} display="block">
              {t("markdownMethodDescription")}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setRecognitionDialogOpen(false)} color="secondary">
            {t("cancel")}
          </MDButton>
          <MDButton onClick={handleRecognitionOptionsConfirm} color="primary">
            {t("startRecognition")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* XML Export Settings Dialog */}
      <Dialog open={xmlExportDialogOpen} onClose={handleXmlExportDialogClose}>
        <DialogTitle>{t("xmlExportSettings")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("selectXmlExportSettings")}</DialogContentText>

          {/* Export type selection */}
          <MDBox mt={2}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              {t("exportType")}
            </MDTypography>
            <RadioGroup
              value={xmlExportType}
              onChange={(e) => setXmlExportType(e.target.value)}
              row
            >
              <FormControlLabel value="agnum" control={<Radio />} label={t("purchases")} />
              <FormControlLabel value="sales" control={<Radio />} label={t("salesInvoices")} />
              <FormControlLabel value="agnum_summary" control={<Radio />} label={t("summary")} />
            </RadioGroup>
          </MDBox>

          {/* PVM Code Override - moved to top */}
          <MDBox mt={2}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              {t("pvmCodeOverride")}
            </MDTypography>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>{t("selectPvmCode")}</InputLabel>
              <Select
                value={overridePvmCode}
                onChange={(e) => setOverridePvmCode(e.target.value)}
                label={t("selectPvmCode")}
              >
                {pvmCodeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MDTypography variant="caption" color="text" mt={1} display="block">
              {t("pvmCodeOverrideDescription")}
            </MDTypography>
          </MDBox>

          <MDBox mt={3}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              Pozymiai
            </MDTypography>
            <FormGroup row>
              {pozymiaiOptions.map((pozymius) => (
                <FormControlLabel
                  key={pozymius.id}
                  control={
                    <Checkbox
                      checked={selectedPozymiai.includes(pozymius.id)}
                      onChange={() => handlePozymiaiToggle(pozymius.id)}
                      size="small"
                    />
                  }
                  label={pozymius.name}
                  sx={{ minWidth: 120, margin: 0.5 }}
                />
              ))}
            </FormGroup>
          </MDBox>
          <MDBox mt={3}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              {t("includePvmInTotal")}
            </MDTypography>
            <RadioGroup
              value={includePvmInTotal ? "true" : "false"}
              onChange={(e) => setIncludePvmInTotal(e.target.value === "true")}
            >
              <FormControlLabel value="true" control={<Radio />} label={t("yes")} />
              <FormControlLabel value="false" control={<Radio />} label={t("no")} />
            </RadioGroup>
          </MDBox>

          {/* Recalculate VAT option */}
          <MDBox mt={3}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              {t("recalcVatTitle")}
            </MDTypography>
            <RadioGroup
              value={recalculateVat ? "true" : "false"}
              onChange={(e) => setRecalculateVat(e.target.value === "true")}
            >
              <FormControlLabel value="true" control={<Radio />} label={t("yes")} />
              <FormControlLabel value="false" control={<Radio />} label={t("no")} />
            </RadioGroup>
            <MDTypography variant="caption" color="text" mt={1} display="block">
              {t("recalcVatDescription")}
            </MDTypography>
          </MDBox>

          {xmlExportWithMatches && (
            <>
              <MDBox mt={3}>
                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  {t("matcherType") || "Matching Method"}
                </MDTypography>
                <RadioGroup
                  value={matcherType}
                  onChange={(e) => setMatcherType(e.target.value)}
                  row
                >
                  <FormControlLabel value="spacy" control={<Radio />} label="spaCy / Fuzzy" />
                  <FormControlLabel value="llm" control={<Radio />} label="LLM (AI)" />
                </RadioGroup>
              </MDBox>

              <MDBox mt={3}>
                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  {t("spacyModelSize")}
                </MDTypography>
                <RadioGroup
                  value={spacyModelSize}
                  onChange={(e) => setSpacyModelSize(e.target.value)}
                >
                  <FormControlLabel value="md" control={<Radio />} label={t("mediumModel")} />
                  <FormControlLabel value="lg" control={<Radio />} label={t("largeModel")} />
                </RadioGroup>
                <MDTypography variant="caption" color="text" mt={1} display="block">
                  {t("spacyModelDescription")}
                </MDTypography>
              </MDBox>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleXmlExportDialogClose} color="secondary">
            {t("cancel")}
          </MDButton>
          <MDButton onClick={handleXmlExportConfirm} color="primary">
            {t("confirm")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DocumentPage;
